import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useChatStore } from "@/stores/chat.store";
import { useAuthStore } from "@/stores/auth.store";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface Client {
  _id: string;
  name: string;
  code: string;
  email?: string;
  phone?: string;
}

interface User {
  _id: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
}

interface NewConversationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedClient?: Client | null;
}

type ConversationType = "client_channel" | "group" | "direct";

export function NewConversationDialog({
  open,
  onOpenChange,
  preselectedClient,
}: NewConversationDialogProps) {
  const { createConversation } = useChatStore();
  const { user } = useAuthStore();
  const [step, setStep] = useState<"type" | "select" | "details">("type");
  const [conversationType, setConversationType] =
    useState<ConversationType>("client_channel");
  const [searchQuery, setSearchQuery] = useState("");
  const [title, setTitle] = useState("");
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (open) {
      if (preselectedClient) {
        setSelectedClient(preselectedClient);
        setConversationType("client_channel");
        setStep("details");
      } else {
        setStep("type");
        setSelectedClient(null);
        setSelectedUsers([]);
        setTitle("");
      }
    }
  }, [open, preselectedClient]);

  useEffect(() => {
    if (step === "select") {
      if (conversationType === "client_channel") {
        fetchClients();
      } else {
        fetchUsers();
      }
    }
  }, [step, conversationType]);

  const fetchClients = async () => {
    setIsLoading(true);
    try {
      const response = await api.get<Client[]>("/crm/clients", {
        params: { search: searchQuery, limit: 20 },
      });
      if (response.success && response.data) {
        // Handle both array response and wrapped response
        const clientsData = Array.isArray(response.data)
          ? response.data
          : (response.data as unknown as { clients: Client[] }).clients || [];
        setClients(clientsData);
      } else {
        setClients([]);
      }
    } catch (error) {
      console.error("Failed to fetch clients:", error);
      setClients([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await api.get<User[]>("/users", {
        params: { search: searchQuery, limit: 20 },
      });
      if (response.success && response.data) {
        // Handle both array response and wrapped response
        const usersData = Array.isArray(response.data)
          ? response.data
          : (response.data as unknown as { users: User[] }).users || [];
        setUsers(usersData);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTypeSelect = (type: ConversationType) => {
    setConversationType(type);
    setStep("select");
  };

  const handleClientSelect = (client: Client) => {
    setSelectedClient(client);
    setTitle(`Chat with ${client.name}`);
    setStep("details");
  };

  const handleUserToggle = (user: User) => {
    setSelectedUsers((prev) => {
      const exists = prev.some((u) => u._id === user._id);
      if (exists) {
        return prev.filter((u) => u._id !== user._id);
      }
      return [...prev, user];
    });
  };

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      let participantIds: string[] = [];

      // Validate current user exists
      if (!user?.id) {
        console.error("Cannot create conversation: Current user ID not found");
        return;
      }

      if (conversationType === "client_channel" && selectedClient) {
        // For client channel: only include current user as participant
        // The client is referenced separately via clientId, not as a participant
        participantIds = [user.id];
      } else if (conversationType === "direct") {
        // For direct messages: include current user + exactly one selected user
        if (selectedUsers.length !== 1) {
          console.error("Direct conversation requires exactly one selected user");
          return;
        }
        const otherUserId = selectedUsers[0]._id;
        // Ensure both users are included and no duplicates
        participantIds = otherUserId === user.id 
          ? [user.id] 
          : [user.id, otherUserId];
      } else if (conversationType === "group") {
        // For group: include current user + all selected users
        if (selectedUsers.length < 2) {
          console.error("Group conversation requires at least 2 selected users");
          return;
        }
        // Start with current user, then add selected users (avoiding duplicates)
        participantIds = [user.id];
        selectedUsers.forEach((u) => {
          if (u._id !== user.id && !participantIds.includes(u._id)) {
            participantIds.push(u._id);
          }
        });
      }

      // Final validation: ensure we have at least one participant
      if (participantIds.length === 0) {
        console.error("Cannot create conversation: No participants");
        return;
      }

      const result = await createConversation({
        type: conversationType,
        title:
          title ||
          (selectedClient ? `Chat with ${selectedClient.name}` : undefined),
        participantIds,
        clientId: selectedClient?._id,
        isClientVisible: conversationType === "client_channel",
      });

      if (result) {
        onOpenChange(false);
        // Reset state
        setStep("type");
        setSelectedClient(null);
        setSelectedUsers([]);
        setTitle("");
      }
    } catch (error) {
      console.error("Failed to create conversation:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const canCreate = () => {
    if (conversationType === "client_channel") {
      return !!selectedClient;
    }
    if (conversationType === "direct") {
      return selectedUsers && selectedUsers.length === 1;
    }
    if (conversationType === "group") {
      return (
        selectedUsers && selectedUsers.length >= 2 && title.trim().length > 0
      );
    }
    return false;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {step === "type" && "New Conversation"}
            {step === "select" &&
              (conversationType === "client_channel"
                ? "Select Client"
                : "Select Team Members")}
            {step === "details" && "Conversation Details"}
          </DialogTitle>
          <DialogDescription>
            {step === "type" &&
              "Choose the type of conversation you want to start"}
            {step === "select" && "Search and select who you want to chat with"}
            {step === "details" && "Configure your conversation settings"}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Select Type */}
        {step === "type" && (
          <div className="space-y-3 py-4">
            <button
              onClick={() => handleTypeSelect("client_channel")}
              className="w-full p-4 text-left border rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">
                    Chat with Client
                  </h4>
                  <p className="text-sm text-gray-500">
                    Start a conversation with a client (visible in their portal)
                  </p>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleTypeSelect("group")}
              className="w-full p-4 text-left border rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Team Group Chat</h4>
                  <p className="text-sm text-gray-500">
                    Create a group conversation with team members
                  </p>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleTypeSelect("direct")}
              className="w-full p-4 text-left border rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Direct Message</h4>
                  <p className="text-sm text-gray-500">
                    Private conversation with a team member
                  </p>
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Step 2: Select Client/Users */}
        {step === "select" && (
          <div className="py-4">
            <div className="relative mb-4">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <Input
                placeholder={
                  conversationType === "client_channel"
                    ? "Search clients..."
                    : "Search team members..."
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Selected users (for group/direct) */}
            {selectedUsers && selectedUsers.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {selectedUsers.map((user) => (
                  <span
                    key={user._id}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                  >
                    {user.name}
                    <button
                      onClick={() => handleUserToggle(user)}
                      className="hover:text-blue-900"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="max-h-64 overflow-y-auto space-y-2">
              {isLoading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : conversationType === "client_channel" ? (
                clients && clients.length > 0 ? (
                  clients.map((client) => (
                    <button
                      key={client._id}
                      onClick={() => handleClientSelect(client)}
                      className="w-full p-3 text-left border rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                    >
                      <div className="font-medium text-gray-900">
                        {client.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {client.code} •{" "}
                        {client.email || client.phone || "No contact"}
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No clients found
                  </div>
                )
              ) : users && users.length > 0 ? (
                users.map((user) => {
                  const isSelected = selectedUsers.some(
                    (u) => u._id === user._id,
                  );
                  return (
                    <button
                      key={user._id}
                      onClick={() => handleUserToggle(user)}
                      className={cn(
                        "w-full p-3 text-left border rounded-lg transition-colors",
                        isSelected
                          ? "border-blue-500 bg-blue-50"
                          : "hover:border-gray-300",
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900">
                            {user.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.email} • {user.role}
                          </div>
                        </div>
                        {isSelected && (
                          <svg
                            className="w-5 h-5 text-blue-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No users found
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => setStep("type")}>
                Back
              </Button>
              {conversationType !== "client_channel" && (
                <Button
                  className="flex-1"
                  onClick={() => setStep("details")}
                  disabled={
                    conversationType === "direct"
                      ? !selectedUsers || selectedUsers.length !== 1
                      : !selectedUsers || selectedUsers.length < 2
                  }
                >
                  Continue
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Details */}
        {step === "details" && (
          <div className="py-4 space-y-4">
            {conversationType === "client_channel" && selectedClient && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Starting chat with</p>
                <p className="font-medium text-gray-900">
                  {selectedClient.name}
                </p>
                <p className="text-sm text-gray-500">{selectedClient.code}</p>
              </div>
            )}

            {conversationType === "group" && (
              <div className="space-y-2">
                <Label htmlFor="title">Group Name *</Label>
                <Input
                  id="title"
                  placeholder="Enter group name"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
            )}

            {(conversationType === "group" || conversationType === "direct") &&
              selectedUsers &&
              selectedUsers.length > 0 && (
                <div>
                  <Label>Participants</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedUsers.map((user) => (
                      <span
                        key={user._id}
                        className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                      >
                        {user.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setStep(preselectedClient ? "type" : "select")}
              >
                Back
              </Button>
              <Button
                className="flex-1"
                onClick={handleCreate}
                disabled={!canCreate() || isCreating}
              >
                {isCreating ? "Creating..." : "Start Conversation"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
